import { DailyEventObjectCameraError } from '@daily-co/daily-js';
import {
  DailyProvider,
  useCallFrame,
  useDailyEvent,
} from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { CustomTrayButtons } from '@web-app/components/Daily/CustomTrayButtons/CustomTrayButtons';
import { RoomErrorScreen } from '@web-app/components/Daily/RoomErrorScreen';
import {
  ROOM_ERROR_TYPES,
  RoomErrorState,
} from '@web-app/components/Daily/RoomErrorScreen/types';
import { useAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import { getJoinBlockedReason } from '@web-app/utils/appointment';
import { extractRoomName } from '@web-app/utils/daily-client';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface DailyCallProviderProps {
  roomUrl: string;
  userName?: string;
  token?: string;
  children: ReactNode;
}

interface DailyCallContextValue {
  wrapperRef: React.RefObject<HTMLDivElement>;
}

const DailyCallContext = createContext<DailyCallContextValue | null>(null);

export const useDailyCall = () => {
  const context = useContext(DailyCallContext);
  if (!context) {
    throw new Error('useDailyCall must be used within DailyCallProvider');
  }
  return context;
};

/**
 * DailyCallProvider wraps Daily.co functionality and provides context for child components.
 * Manages the call frame, auto-join logic, and provides a ref for the iframe container.
 *
 * Appointment status check:
 * - Fetches appointment data internally using roomName (React Query caches this)
 * - Before attempting to join, checks if appointment status is 'Occurred' AND end time has passed
 * - Shows appropriate error screen for patient/provider if session has ended
 *
 * Room verification is handled by useVerifyRoom in the layout layer
 * (ProviderCallLayout / PatientCallLayout) before this component renders.
 * If Daily returns an error during join, a generic error screen is shown.
 *
 * Custom tray buttons:
 * - If token is present (provider view), adds "End Session" button to Daily's tray
 * - Button click is handled by child components via 'custom-button-click' event
 */
export const DailyCallProvider: FC<DailyCallProviderProps> = ({
  roomUrl,
  userName,
  token,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [roomError, setRoomError] = useState<RoomErrorState | null>(null);

  const roomName = useMemo(() => extractRoomName(roomUrl), [roomUrl]);

  // Fetch appointment data for pre-join status check (React Query caches this)
  const { data: appointment } = useAppointmentByRoomId(roomName ?? undefined);

  const callFrame = useCallFrame({
    parentElRef: wrapperRef as React.MutableRefObject<HTMLElement>,
    options: {
      dailyConfig: {
        useDevicePreferenceCookies: true,
      },
      url: roomUrl,
      theme: {
        colors: {
          // Light theme configuration matching Chakra light theme
          accent: '#003E32', // green.500
          accentText: '#FFFFFF',
          background: '#FFFFFF', // bg.default
          backgroundAccent: '#F7FAFC', // surface.light (gray.50)
          baseText: '#1A202C', // text.default (gray.800)
          border: '#E2E8F0', // border.default (gray.200)
          mainAreaBg: '#FFFFFF', // surface.default
          mainAreaBgAccent: '#EDF2F7', // surface.dark (gray.100)
          mainAreaText: '#1A202C', // text.default (gray.800)
          supportiveText: '#4A5568', // text.muted (gray.600)
        },
      },
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: '0',
        position: 'absolute',
        top: '0',
        left: '0',
      },
      cssText: `
          .daily-record-toggle {
            display: none !important;
          }
          [class*="record"] {
            display: none !important;
          }
        `,
      ...(userName && {
        userName,
      }),
      ...(token && {
        token,
      }),
    },
    shouldCreateInstance: () => Boolean(wrapperRef.current),
  });

  /**
   * Handles Daily errors by showing an error screen.
   * Room verification and recreation is handled by useVerifyRoom in the layout layer
   * (ProviderCallLayout / PatientCallLayout) before DailyCallProvider renders.
   */
  const handleDailyError = useCallback(
    (errorMsg: string, errorType?: string) => {
      datadogLogs.logger.error(
        'Daily error',
        callLogContext('call-error', {
          roomId: roomName ?? roomUrl,
          errorMsg,
          errorType,
        }),
      );

      setRoomError({
        type: ROOM_ERROR_TYPES.UNKNOWN_ERROR,
        message: errorMsg,
      });
    },
    [roomName, roomUrl],
  );

  // Join the call (with pre-join appointment status check)
  useEffect(() => {
    if (!callFrame) return;

    // Check if appointment is invalid before attempting to join
    const isProvider = Boolean(token);
    const joinBlockedReason = getJoinBlockedReason(appointment, isProvider);

    if (joinBlockedReason) {
      datadogLogs.logger.info(
        'Appointment is blocked, preventing join',
        callLogContext('call-join', {
          roomId: roomName ?? roomUrl,
          participantType: isProvider ? 'provider' : 'patient',
          errorType: joinBlockedReason,
          appointmentStatus: appointment?.status,
          appointmentEnd: appointment?.end,
        }),
      );
      setRoomError({ type: joinBlockedReason });
      return;
    }

    callFrame?.join().catch((err) => {
      const dailyErr = err as FatalErrorEvent;
      const errorMsg =
        dailyErr?.errorMsg ||
        (err instanceof Error ? err.message : String(err));
      const errorType = dailyErr?.error?.type;

      datadogLogs.logger.error(
        'Failed to join call',
        callLogContext('call-join', {
          roomId: roomName ?? roomUrl,
          roomUrl,
          errorMsg,
          errorType,
          errorDetails: dailyErr?.error?.details,
        }),
        err instanceof Error ? err : undefined,
      );
      handleDailyError(errorMsg, errorType);
    });
  }, [callFrame, handleDailyError, roomUrl, appointment, token, roomName]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const contextValue = useMemo(() => ({ wrapperRef }), []);

  return (
    <DailyProvider callObject={callFrame}>
      <DailyCallEventLogger
        roomUrl={roomUrl}
        participantType={token ? 'provider' : 'patient'}
      />
      {/* Show error screen for room issues */}
      {roomError ? (
        <RoomErrorScreen state={roomError} onReload={handleReload} />
      ) : (
        <DailyCallContext.Provider value={contextValue}>
          <CustomTrayButtons />
          {children}
        </DailyCallContext.Provider>
      )}
    </DailyProvider>
  );
};

interface FatalErrorEvent {
  errorMsg?: string;
  error?: { type?: string; msg?: string; details?: Record<string, unknown> };
}

interface NonFatalErrorEvent {
  type?: string;
  errorMsg?: string;
  details?: Record<string, unknown>;
}

/**
 * Internal component to log Daily call events.
 * Must be rendered inside DailyProvider to use useDailyEvent hooks.
 */
const DailyCallEventLogger: FC<{
  roomUrl: string;
  participantType: 'provider' | 'patient';
}> = ({ roomUrl, participantType }) => {
  const roomId = extractRoomName(roomUrl) ?? roomUrl;

  // Log fatal errors (disconnect user from call)
  // Distinguish ejected (expected business flow) from genuine fatal errors
  const handleError = useCallback(
    (event: FatalErrorEvent | null) => {
      const context = callLogContext('call-error', {
        roomId,
        roomUrl,
        participantType,
        errorMsg: event?.errorMsg,
        errorType: event?.error?.type,
        errorDetails: event?.error?.details,
      });

      if (event?.error?.type === 'ejected') {
        datadogLogs.logger.warn('Participant ejected', context);
      } else {
        datadogLogs.logger.error('Fatal error', context);
      }
    },
    [roomUrl, roomId, participantType],
  );

  // Log non-fatal errors (quality degradation, processor issues)
  const handleNonFatalError = useCallback(
    (event: NonFatalErrorEvent | null) => {
      datadogLogs.logger.warn(
        'Non-fatal error',
        callLogContext('call-error', {
          roomId,
          roomUrl,
          participantType,
          type: event?.type,
          errorMsg: event?.errorMsg,
          details: event?.details,
        }),
      );
    },
    [roomUrl, roomId, participantType],
  );

  // Log device failures (participant has no working camera and/or microphone)
  const handleCameraError = useCallback(
    (event: DailyEventObjectCameraError | null) => {
      const error = event?.error;

      datadogLogs.logger.error(
        'Camera or microphone error',
        callLogContext('call-error', {
          roomId,
          roomUrl,
          participantType,
          detail:
            'getUserMedia failed, so this participant has no working camera and/or microphone',
          errorType: error?.type,
          errorMsg: event?.errorMsg?.errorMsg,
          audioOk: event?.errorMsg?.audioOk,
          videoOk: event?.errorMsg?.videoOk,
          // Separates "participant pressed Deny" from "the browser or OS
          // blocked us", which need different support instructions.
          blockedBy:
            error?.type === 'permissions' ? error.blockedBy : undefined,
          blockedMedia:
            error?.type === 'permissions' ? error.blockedMedia : undefined,
        }),
      );
    },
    [roomUrl, roomId, participantType],
  );

  useDailyEvent('error', handleError);
  useDailyEvent('nonfatal-error', handleNonFatalError);
  useDailyEvent('camera-error', handleCameraError);

  return null;
};
